/**
 * O MAPA DE NAVEGAÇÃO DO CERES (CERES-UX-001).
 *
 * Até a 0.209 o menu era a organização dos MÓDULOS — seis domínios, vinte e dois
 * destinos com o mesmo peso, e quem abria o CRM precisava decidir onde
 * trabalhar antes de descobrir o que fazer. A nova arquitetura tem CINCO
 * jornadas e um "Mais":
 *
 *   Hoje · Pré-vendas · Negócios · Conversas · Relacionamentos · Mais
 *
 * NADA SAIU DO PRODUTO. O que deixou o primeiro nível continua com a MESMA
 * rota, a MESMA permissão e a MESMA capacidade de antes, em um destes lugares:
 *
 * - `GRUPOS_DO_MAIS`: módulos satélites, registros contextuais, gestão,
 *   configuração e ajuda — o lançador do "Mais", filtrado por permissão E
 *   capacidade (módulo não contratado não aparece como se estivesse
 *   disponível);
 * - `DESTINOS_DE_CONTEXTO`: telas que se alcançam de dentro das jornadas
 *   (lista de empresas, contatos, Kanban, lista de oportunidades, Cliente
 *   ideal). Não são item de menu, mas continuam declaradas aqui porque o
 *   bloqueio de URL por capacidade (`capacidade-da-rota.ts`) é DERIVADO desta
 *   lista — tirar a rota daqui abriria a URL para quem não contratou.
 *
 * Um mapa só: o menu, o lançador do "Mais" e o gate de URL leem a mesma lista.
 */

import type { ChaveDeTexto } from '../lib/i18n';
import {
  IconBriefcase,
  IconBuilding,
  IconCalendar,
  IconChart,
  IconCheck,
  IconClock,
  IconConversa,
  IconHandshake,
  IconHoje,
  IconInbox,
  IconKanban,
  IconMais,
  IconNote,
  IconSettings,
  IconShield,
  IconSpark,
  IconTag,
  IconTarget,
  IconTrend,
  IconUsers,
} from './ui';
import { IconHelp } from './help';

export type IconeDoItem = (props: { className?: string }) => JSX.Element;

/** Uma alternativa de acesso: basta UMA delas para o item aparecer. */
export interface Acesso {
  permission?: string;
  feature?: string;
}

export interface NavItem {
  to: string;
  /** Rótulo traduzido (§20.5). */
  chave: ChaveDeTexto;
  /** Rótulo em português, para leitura do código e dos testes. */
  label: string;
  /** Linha curta de orientação, mostrada no menu expandido e no lançador. */
  ajuda?: ChaveDeTexto;
  icon: IconeDoItem;
  /** Permissão necessária para o item aparecer. */
  permission?: string;
  /** Qualquer uma destas permissões basta (Administração). */
  permissoesAlternativas?: readonly string[];
  /** Capacidade de produto necessária além da permissão. */
  feature?: string;
  /**
   * Jornada que reúne mais de um canal (Conversas): o item aparece quando
   * QUALQUER um dos acessos está liberado. A página trata cada parte sozinha.
   */
  algumDe?: readonly Acesso[];
  end?: boolean;
  /**
   * Fail-closed reforçado (0.131.0): além de permissão e capacidade, exige a
   * sessão resolvida. Em carregamento, erro ou ausência, o item não aparece.
   */
  exigeSessaoPronta?: boolean;
  /**
   * Prefixos que também marcam este item como o atual: a ficha de uma empresa
   * pertence a Relacionamentos, o Kanban pertence a Negócios.
   */
  ativoEm?: readonly string[];
}

export interface GrupoDoMais {
  chave: 'modulos' | 'registros' | 'gestao' | 'configuracao';
  titulo: ChaveDeTexto;
  itens: NavItem[];
}

/** Hoje: a fila de trabalho. Fica na raiz porque é onde o dia começa. */
export const HOJE_NAV: NavItem = {
  to: '/',
  chave: 'ux.nav.hoje',
  label: 'Hoje',
  ajuda: 'ux.nav.hojeAjuda',
  icon: IconHoje,
  permission: 'dashboard:read',
  feature: 'basic_dashboard',
  end: true,
};

/** As cinco jornadas, na ordem do trabalho. */
export const NAV_PRINCIPAL: NavItem[] = [
  HOJE_NAV,
  {
    // CERES-022 manteve UMA carteira de pré-vendas; o Cliente ideal mora nela
    // desde a CERES-UX-001 (antes, sozinho num grupo "Inteligência").
    to: '/pre-vendas',
    chave: 'ux.nav.preVendas',
    label: 'Pré-vendas',
    ajuda: 'ux.nav.preVendasAjuda',
    icon: IconTarget,
    permission: 'pre_sales:read',
    feature: 'pre_sales',
    ativoEm: ['/icp'],
  },
  {
    to: '/negocios',
    chave: 'ux.nav.negocios',
    label: 'Negócios',
    ajuda: 'ux.nav.negociosAjuda',
    icon: IconChart,
    permission: 'opportunity:read',
    feature: 'opportunities',
    ativoEm: ['/oportunidades', '/pipeline'],
  },
  {
    to: '/conversas',
    chave: 'ux.nav.conversas',
    label: 'Conversas',
    ajuda: 'ux.nav.conversasAjuda',
    icon: IconConversa,
    algumDe: [
      { permission: 'communication:read', feature: 'communications' },
      { permission: 'meeting:read', feature: 'meetings' },
      { feature: 'whatsapp' },
    ],
  },
  {
    to: '/relacionamentos',
    chave: 'ux.nav.relacionamentos',
    label: 'Relacionamentos',
    ajuda: 'ux.nav.relacionamentosAjuda',
    icon: IconUsers,
    permission: 'account:read',
    feature: 'companies',
    ativoEm: ['/empresas', '/contatos'],
  },
];

/**
 * O QUE MORA NO "MAIS".
 *
 * Satélites primeiro (só aparecem contratados), depois os registros que vivem
 * no contexto das jornadas mas continuam tendo visão global, a gestão e a
 * configuração. Cada item com a permissão e a capacidade que já tinha.
 */
export const GRUPOS_DO_MAIS: GrupoDoMais[] = [
  {
    chave: 'modulos',
    titulo: 'ux.mais.grupoModulos',
    itens: [
      {
        to: '/customer-success',
        chave: 'nav.customerSuccess',
        label: 'Customer Success',
        ajuda: 'ux.mais.csAjuda',
        icon: IconHandshake,
        permission: 'cs:read',
        feature: 'customer_success',
      },
      {
        to: '/parceiros',
        chave: 'nav.parceiros',
        label: 'Parceiros',
        ajuda: 'ux.mais.parceirosAjuda',
        icon: IconHandshake,
        permission: 'partner:read',
        feature: 'partner_management',
      },
      {
        to: '/reservas',
        chave: 'nav.reservas',
        label: 'Reservas de conta',
        ajuda: 'ux.mais.reservasAjuda',
        icon: IconShield,
        permission: 'reservation:read',
        feature: 'reservations',
      },
      {
        // Comissão é dado sensível: só com a sessão PRONTA.
        to: '/comissoes',
        chave: 'nav.comissoes',
        label: 'Comissões',
        ajuda: 'ux.mais.comissoesAjuda',
        icon: IconHandshake,
        permission: 'commission:read',
        feature: 'commissions',
        exigeSessaoPronta: true,
      },
    ],
  },
  {
    chave: 'registros',
    titulo: 'ux.mais.grupoRegistros',
    itens: [
      {
        to: '/atividades',
        chave: 'nav.atividades',
        label: 'Atividades',
        ajuda: 'ux.mais.atividadesAjuda',
        icon: IconCalendar,
        permission: 'activity:read',
        feature: 'activities',
      },
      {
        // CERES-036: o registro documental é do núcleo (`proposal_registry`).
        to: '/propostas',
        chave: 'nav.propostas',
        label: 'Propostas',
        ajuda: 'ux.mais.propostasAjuda',
        icon: IconNote,
        permission: 'proposal:read',
        feature: 'proposal_registry',
      },
      {
        to: '/contratos',
        chave: 'nav.contratos',
        label: 'Contratos',
        ajuda: 'ux.mais.contratosAjuda',
        icon: IconShield,
        permission: 'contract:read',
        feature: 'contracts',
      },
      {
        to: '/cadencias',
        chave: 'nav.cadencias',
        label: 'Cadências',
        ajuda: 'ux.mais.cadenciasAjuda',
        icon: IconClock,
        permission: 'cadence:read',
        feature: 'cadences',
      },
      {
        to: '/reunioes',
        chave: 'nav.reunioes',
        label: 'Reuniões',
        ajuda: 'ux.mais.reunioesAjuda',
        icon: IconNote,
        permission: 'meeting:read',
        feature: 'meetings',
      },
      {
        to: '/caixa-de-entrada',
        chave: 'nav.caixaDeEntrada',
        label: 'Caixa de entrada',
        ajuda: 'ux.mais.caixaAjuda',
        icon: IconInbox,
        permission: 'communication:read',
        feature: 'communications',
      },
      {
        to: '/agentes',
        chave: 'nav.agentes',
        label: 'Agentes',
        ajuda: 'ux.mais.agentesAjuda',
        icon: IconSpark,
        permission: 'agent:use',
        feature: 'ai_assistant',
      },
    ],
  },
  {
    chave: 'gestao',
    titulo: 'ux.mais.grupoGestao',
    itens: [
      {
        to: '/receita',
        chave: 'nav.receita',
        label: 'Receita e forecast',
        ajuda: 'ux.mais.receitaAjuda',
        icon: IconTrend,
        permission: 'forecast:read',
        feature: 'forecasting',
      },
      {
        to: '/relatorios',
        chave: 'nav.relatorios',
        label: 'Relatórios',
        ajuda: 'ux.mais.relatoriosAjuda',
        icon: IconChart,
        permission: 'opportunity:read',
        feature: 'advanced_analytics',
      },
      {
        to: '/aprovacoes',
        chave: 'nav.aprovacoes',
        label: 'Aprovações',
        ajuda: 'ux.mais.aprovacoesAjuda',
        icon: IconCheck,
        permission: 'approval:grant',
        feature: 'workflows',
      },
    ],
  },
  {
    chave: 'configuracao',
    titulo: 'ux.mais.grupoConfiguracao',
    itens: [
      {
        // CERES-028: UMA central para o que se vende e como se vende.
        to: '/catalogo-comercial',
        chave: 'nav.catalogoComercial',
        label: 'Catálogo comercial',
        ajuda: 'ux.mais.catalogoAjuda',
        icon: IconTag,
        permission: 'catalog:read',
        feature: 'commercial_catalog',
      },
      {
        to: '/admin',
        chave: 'ux.mais.administracao',
        label: 'Administração',
        ajuda: 'ux.mais.administracaoAjuda',
        icon: IconSettings,
        // As mesmas permissões que abrem `/admin` no roteador.
        permissoesAlternativas: [
          'pipeline:configure',
          'user:manage',
          'policy:read',
          'audit:read',
          'demo:manage',
          'feature:manage',
          'event:read',
        ],
        ativoEm: ['/admin'],
      },
      {
        // Ajuda: sempre visível, sem exigir permissão.
        to: '/ajuda',
        chave: 'nav.centralDeAjuda',
        label: 'Central de Ajuda',
        ajuda: 'ux.mais.ajudaAjuda',
        icon: IconHelp,
      },
    ],
  },
];

/** O "Mais" no menu: o lançador dos grupos acima. */
export const MAIS_NAV: NavItem = {
  to: '/mais',
  chave: 'ux.nav.mais',
  label: 'Mais',
  ajuda: 'ux.nav.maisAjuda',
  icon: IconMais,
  ativoEm: GRUPOS_DO_MAIS.flatMap((grupo) =>
    grupo.itens.flatMap((item) => [item.to, ...(item.ativoEm ?? [])]),
  ),
};

/**
 * Destinos alcançados de dentro das jornadas, sem item próprio de menu.
 * Declarados para que o gate de URL por capacidade continue valendo.
 */
export const DESTINOS_DE_CONTEXTO: NavItem[] = [
  {
    to: '/empresas',
    chave: 'nav.empresas',
    label: 'Empresas',
    icon: IconBuilding,
    permission: 'account:read',
    feature: 'companies',
  },
  {
    to: '/contatos',
    chave: 'nav.contatos',
    label: 'Contatos',
    icon: IconUsers,
    permission: 'contact:read',
    feature: 'contacts',
  },
  {
    to: '/oportunidades',
    chave: 'nav.oportunidades',
    label: 'Oportunidades',
    icon: IconBriefcase,
    permission: 'opportunity:read',
    feature: 'opportunities',
  },
  {
    to: '/pipeline',
    chave: 'nav.pipeline',
    label: 'Pipeline',
    icon: IconKanban,
    permission: 'opportunity:read',
    feature: 'pipeline',
  },
  {
    to: '/icp',
    chave: 'nav.icp',
    label: 'Cliente ideal',
    icon: IconTarget,
    permission: 'icp:read',
    feature: 'scoring',
  },
];

/**
 * Todos os destinos numa lista só — a mesma que `capacidade-da-rota.ts` usa para
 * barrar a URL digitada. Um mapa, nunca dois.
 */
export const ITENS_DE_NAVEGACAO: NavItem[] = [
  ...NAV_PRINCIPAL,
  ...GRUPOS_DO_MAIS.flatMap((grupo) => grupo.itens),
  ...DESTINOS_DE_CONTEXTO,
];

export interface Autorizacao {
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  hasFeature: (feature: string) => boolean;
  sessaoPronta: boolean;
}

function acessoLiberado(acesso: Acesso, { can, hasFeature }: Autorizacao): boolean {
  return (
    (acesso.permission === undefined || can(acesso.permission)) &&
    (acesso.feature === undefined || hasFeature(acesso.feature))
  );
}

export function itemAutorizado(item: NavItem, autorizacao: Autorizacao): boolean {
  if (item.exigeSessaoPronta === true && !autorizacao.sessaoPronta) return false;
  if (item.algumDe !== undefined && !item.algumDe.some((a) => acessoLiberado(a, autorizacao))) {
    return false;
  }
  if (
    item.permissoesAlternativas !== undefined &&
    !autorizacao.canAny([...item.permissoesAlternativas])
  ) {
    return false;
  }
  return acessoLiberado({ permission: item.permission, feature: item.feature }, autorizacao);
}

export function itensAutorizados(items: NavItem[], autorizacao: Autorizacao): NavItem[] {
  return items.filter((item) => itemAutorizado(item, autorizacao));
}

function casaPrefixo(prefixo: string, pathname: string): boolean {
  return pathname === prefixo || pathname.startsWith(`${prefixo}/`);
}

/** O item é o "lugar atual" da pessoa? */
export function estaAtivo(item: NavItem, pathname: string): boolean {
  if (item.end === true) return pathname === item.to;
  if (casaPrefixo(item.to, pathname)) return true;
  return (item.ativoEm ?? []).some((prefixo) => casaPrefixo(prefixo, pathname));
}

/** A jornada (ou o "Mais") a que o caminho atual pertence. */
export function jornadaDoCaminho(pathname: string): NavItem | null {
  return [...NAV_PRINCIPAL, MAIS_NAV].find((item) => estaAtivo(item, pathname)) ?? null;
}
